import * as React from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";

const Drawer = Dialog;

function DrawerTrigger(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} />;
}

interface DrawerOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DialogBackdrop> {}

function DrawerOverlay({ className, ...props }: DrawerOverlayProps) {
  return (
    <DialogBackdrop
      transition
      className={cn(
        "data-closed:opacity-0 fixed inset-0 bg-gray-900/50 transition-opacity duration-500 ease-in-out",
        className,
      )}
      {...props}
    />
  );
}

interface DrawerContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof DialogPanel>, "children"> {
  showCloseButton?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
}

function DrawerContent({
  className,
  children,
  showCloseButton = true,
  onClose,
  ...props
}: DrawerContentProps) {
  return (
    <div className="fixed inset-0 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="pointer-events-none fixed inset-y-6 right-6 flex max-w-full pl-10 sm:pl-16">
          <DialogPanel
            transition
            className={cn(
              "data-closed:translate-x-full pointer-events-auto relative w-screen max-w-4xl transform rounded-xl transition duration-500 ease-in-out sm:duration-700",
              className,
            )}
            {...props}
          >
            {showCloseButton && onClose ? (
              <TransitionChild>
                <div className="data-closed:opacity-0 absolute left-0 top-0 -ml-8 flex pr-2 pt-4 duration-500 ease-in-out sm:-ml-10 sm:pr-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="relative rounded-md text-gray-400 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                  >
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="size-6" />
                  </button>
                </div>
              </TransitionChild>
            ) : null}

            <div className="relative h-full overflow-hidden rounded-2xl bg-white shadow-xl">
              {children}
            </div>
          </DialogPanel>
        </div>
      </div>
    </div>
  );
}

function DrawerHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm",
        className,
      )}
      {...props}
    />
  );
}

function DrawerBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex-1 overflow-y-auto px-6 py-6", className)}
      {...props}
    />
  );
}

function DrawerTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn("text-xl font-semibold text-gray-900", className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm text-gray-500", className)} {...props} />;
}

export {
  Drawer,
  DrawerTrigger,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
};
